import React from 'react';
import {
  normalizeSlideTextFontFamily,
  type ProgramSlideQuizChoiceOptionV1,
  type ProgramSlideQuizChoiceSetV1,
} from '../../types/programContentSlideCanvas';
import { ProgramSlideFloatedToolbar } from './ProgramSlideFloatedToolbar';
import { ProgramSlideTrashDeleteControl } from './ProgramSlideTrashDeleteControl';

export type ProgramSlideQuizChoiceLayerProps = {
  sets: ProgramSlideQuizChoiceSetV1[];
  options: ProgramSlideQuizChoiceOptionV1[];
  viewMode: boolean;
  canEdit: boolean;
  hasMultiSelection: boolean;
  holdingDeleteId: string | null;
  deleteConfirmSetId: string | null;
  choicePickedBySetId: Record<string, Set<number>>;
  choiceSucceededSetIds: Set<string>;
  choiceWrongFlashSetIds: Set<string>;
  editingLabelOptionId: string | null;
  choiceMinOptions: number;
  choiceMaxOptions: number;
  getBlockLayerZIndex: (baseZ: number, toolbarOpen: boolean) => number;
  resolveToolbarPlacement: (
    blockY: number,
    blockH: number,
    toolbarHeightPx: number
  ) => { className: string; style: React.CSSProperties };
  onToggleCorrectIndex: (setId: string, index: number) => void;
  onAddOption: (setId: string) => void;
  onRemoveOption: (setId: string) => void;
  onStartDeleteHold: (e: React.PointerEvent, setId: string) => void;
  /** 짧게 떼면 true → 확인 미니모달, leave/cancel은 false */
  onEndDeleteHold: (openConfirmIfShort: boolean) => void;
  onCancelDeleteConfirm: () => void;
  onConfirmDelete: () => void;
  onStartDrag: (e: React.PointerEvent, option: ProgramSlideQuizChoiceOptionV1) => void;
  onToggleViewPick: (setId: string, index: number) => void;
  onSelectOption: (optionId: string) => void;
  onMultiSelectPointerDown: (e: React.PointerEvent, optionId: string) => boolean;
  onEditLabel: (optionId: string) => void;
  onPatchLabel: (optionId: string, label: string) => void;
  onEndEditLabel: () => void;
  isOptionSelected: (optionId: string) => boolean;
  isSetPeerActive: (setId: string) => boolean;
  /** 슬라이드 블록 그룹 단위 선택 중일 때의 `groupId`(선택지 `option.groupId`와 비교) */
  selectedSlideBlockGroupId?: string | null;
  slideTextFontCssFamily: (fontFamily: ReturnType<typeof normalizeSlideTextFontFamily> | undefined) => string;
  toolbarRowHeightPx: number;
  mapBackgroundColor?: (hex: string) => string;
  canvasInnerEl?: HTMLElement | null;
  activeToolbarZIndex?: number;
};

export function ProgramSlideQuizChoiceLayer({
  sets,
  options,
  viewMode,
  canEdit,
  hasMultiSelection,
  holdingDeleteId,
  deleteConfirmSetId,
  choicePickedBySetId,
  choiceSucceededSetIds,
  choiceWrongFlashSetIds,
  editingLabelOptionId,
  choiceMinOptions,
  choiceMaxOptions,
  getBlockLayerZIndex,
  resolveToolbarPlacement,
  onToggleCorrectIndex,
  onAddOption,
  onRemoveOption,
  onStartDeleteHold,
  onEndDeleteHold,
  onCancelDeleteConfirm,
  onConfirmDelete,
  onStartDrag,
  onToggleViewPick,
  onSelectOption,
  onMultiSelectPointerDown,
  onEditLabel,
  onPatchLabel,
  onEndEditLabel,
  isOptionSelected,
  isSetPeerActive,
  selectedSlideBlockGroupId,
  slideTextFontCssFamily,
  toolbarRowHeightPx,
  mapBackgroundColor,
  canvasInnerEl,
  activeToolbarZIndex = 1015,
}: ProgramSlideQuizChoiceLayerProps) {
  const dispBg = mapBackgroundColor ?? ((hex: string) => hex);
  return (
    <>
      {options.map((option) => {
        const set = sets.find((s) => s.id === option.setId);
        if (!set) return null;
        const setOptions = options
          .filter((o) => o.setId === option.setId)
          .sort((a, b) => a.optionIndex - b.optionIndex);
        const isSelected = isOptionSelected(option.id);
        const isPeerActive = !isSelected && isSetPeerActive(option.setId);
        const setHasSelection = setOptions.some((o) => isOptionSelected(o.id));
        const isToolbarOpen = setHasSelection && canEdit && !hasMultiSelection && option.optionIndex === 0;
        const isEditingLabel = editingLabelOptionId === option.id;
        const isCorrectInEdit = canEdit && (set.correctOptionIndices ?? []).includes(option.optionIndex);
        const isPicked = viewMode && (choicePickedBySetId[option.setId]?.has(option.optionIndex) ?? false);
        const isSucceeded = choiceSucceededSetIds.has(option.setId);
        const isWrongFlash = choiceWrongFlashSetIds.has(option.setId);
        const radius = option.cornerRadiusPx ?? 8;
        return (
          <div
            key={option.id}
            className={
              'cp-program-slide-canvas-quiz-choice-option' +
              (isSelected && canEdit ? ' is-option-selected' : '') +
              (isPeerActive && canEdit ? ' is-set-peer-active' : '') +
              (isPicked ? ' is-picked' : '') +
              (isWrongFlash ? ' is-wrong-flash' : '') +
              (isSucceeded ? ' is-succeeded' : '')
            }
            style={{
              left: `${option.x * 100}%`,
              top: `${option.y * 100}%`,
              width: `${option.w * 100}%`,
              height: `${option.h * 100}%`,
              backgroundColor: dispBg(option.backgroundColorHex),
              borderRadius: `${radius}px`,
              zIndex: getBlockLayerZIndex(10 + option.zIndex, isToolbarOpen),
            }}
            onPointerDown={(e) => {
              if (viewMode) {
                e.stopPropagation();
                if (!isSucceeded) onToggleViewPick(option.setId, option.optionIndex);
                return;
              }
              if (!canEdit || isEditingLabel) return;
              if (onMultiSelectPointerDown(e, option.id)) return;
              const slideGid = option.groupId?.trim() ?? '';
              if (
                selectedSlideBlockGroupId &&
                slideGid &&
                selectedSlideBlockGroupId === slideGid
              ) {
                onStartDrag(e, option);
                return;
              }
              onSelectOption(option.id);
              onStartDrag(e, option);
            }}
            onDoubleClick={(e) => {
              if (!canEdit || viewMode) return;
              e.stopPropagation();
              onEditLabel(option.id);
            }}
          >
            {isToolbarOpen ? (
              <ProgramSlideFloatedToolbar
                host={canvasInnerEl}
                blockX={option.x}
                blockY={option.y}
                blockH={option.h}
                className="cp-program-slide-canvas-toolbar cp-program-slide-canvas-quiz-choice-toolbar"
                placement={resolveToolbarPlacement(option.y, option.h, toolbarRowHeightPx)}
                zIndex={activeToolbarZIndex}
                onPointerDown={(e) => e.stopPropagation()}
              >
                <div className="cp-program-slide-canvas-quiz-choice-toolbar-row">
                  <span className="cp-program-slide-canvas-tl">정답</span>
                  {setOptions.map((opt) => (
                    <button
                      key={opt.id}
                      type="button"
                      className={
                        'cp-program-slide-canvas-quiz-choice-answer-toggle' +
                        ((set.correctOptionIndices ?? []).includes(opt.optionIndex) ? ' is-on' : '')
                      }
                      onClick={() => onToggleCorrectIndex(set.id, opt.optionIndex)}
                      aria-pressed={(set.correctOptionIndices ?? []).includes(opt.optionIndex)}
                      aria-label={`${opt.optionIndex + 1}번 정답`}
                    >
                      {opt.optionIndex + 1}번
                    </button>
                  ))}
                  <div
                    className="cp-program-slide-canvas-quiz-choice-count-actions"
                    role="group"
                    aria-label="선택지 개수 조절"
                  >
                    <button
                      type="button"
                      className="cp-program-slide-canvas-quiz-choice-count-btn"
                      onClick={() => onAddOption(set.id)}
                      disabled={setOptions.length >= choiceMaxOptions}
                      aria-label="선택지 추가"
                      title={setOptions.length >= choiceMaxOptions ? `최대 ${choiceMaxOptions}개` : '선택지 추가'}
                    >
                      <span
                        className="cp-program-slide-canvas-quiz-choice-count-btn-icon"
                        aria-hidden
                        style={{ WebkitMaskImage: 'url(/Plus.svg)', maskImage: 'url(/Plus.svg)' }}
                      />
                    </button>
                    <button
                      type="button"
                      className="cp-program-slide-canvas-quiz-choice-count-btn"
                      onClick={() => onRemoveOption(set.id)}
                      disabled={setOptions.length <= choiceMinOptions}
                      aria-label="선택지 삭제"
                      title={setOptions.length <= choiceMinOptions ? `최소 ${choiceMinOptions}개` : '선택지 삭제'}
                    >
                      <span
                        className="cp-program-slide-canvas-quiz-choice-count-btn-icon"
                        aria-hidden
                        style={{ WebkitMaskImage: 'url(/Minus.svg)', maskImage: 'url(/Minus.svg)' }}
                      />
                    </button>
                  </div>
                  <ProgramSlideTrashDeleteControl
                    isHolding={holdingDeleteId === set.id}
                    showConfirm={deleteConfirmSetId === set.id}
                    ariaLabel="선택형 퀴즈 세트 삭제: 짧게 확인, 길게 바로 삭제"
                    onPointerDown={(e) => onStartDeleteHold(e, set.id)}
                    onPointerUp={() => onEndDeleteHold(true)}
                    onPointerLeave={() => onEndDeleteHold(false)}
                    onPointerCancel={() => onEndDeleteHold(false)}
                    onCancelConfirm={onCancelDeleteConfirm}
                    onConfirmDelete={onConfirmDelete}
                  />
                </div>
              </ProgramSlideFloatedToolbar>
            ) : null}
            {isCorrectInEdit ? (
              <span className="cp-program-slide-canvas-quiz-choice-correct-badge" aria-hidden title="정답">
                <svg viewBox="0 0 24 24" aria-hidden focusable="false">
                  <path d="M9.5 16.2 5.8 12.5l-1.4 1.4L9.5 19 20 8.5l-1.4-1.4z" fill="currentColor" />
                </svg>
              </span>
            ) : null}
            {isPicked ? (
              <span className="cp-program-slide-canvas-quiz-choice-picked-badge" aria-hidden title="선택됨">
                <svg viewBox="0 0 24 24" aria-hidden focusable="false">
                  <path d="M9.5 16.2 5.8 12.5l-1.4 1.4L9.5 19 20 8.5l-1.4-1.4z" fill="currentColor" />
                </svg>
              </span>
            ) : null}
            {isEditingLabel ? (
              <input
                type="text"
                className="cp-program-slide-canvas-quiz-choice-label-input"
                style={{
                  fontSize: `${option.fontSizePx}px`,
                  fontFamily: slideTextFontCssFamily(option.fontFamily),
                  color: option.colorHex,
                  fontWeight: option.bold ? 700 : 600,
                  borderRadius: `${radius}px`,
                }}
                value={option.label}
                autoFocus
                onChange={(e) => onPatchLabel(option.id, e.target.value)}
                onBlur={onEndEditLabel}
                onKeyDown={(e) => {
                  e.stopPropagation();
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    onEndEditLabel();
                  }
                }}
                onPointerDown={(e) => e.stopPropagation()}
                spellCheck={false}
                aria-label="선택지 텍스트 편집"
              />
            ) : (
              <div
                className="cp-program-slide-canvas-quiz-choice-label"
                style={{
                  fontSize: `${option.fontSizePx}px`,
                  fontFamily: slideTextFontCssFamily(option.fontFamily),
                  color: option.colorHex,
                  fontWeight: option.bold ? 700 : 600,
                  borderRadius: `${radius}px`,
                }}
              >
                {option.label}
              </div>
            )}
          </div>
        );
      })}
    </>
  );
}
